import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetTourOffer } from '~/hooks/apis/tours/useTourOffers';

import { Bundle, HotelBundleComponent, TourBundleComponent } from '~/types/bundles/bundle';

import {
  Market,
  hotelOnSaleMarkets,
  marketBrands,
  marketRegions,
  sharedMarkets,
  tourScheduledMarkets,
} from './regions';

/** The codes the tour and hotel share, and the ones the creator kept. Nothing kept means not narrowed. */
function narrow(shared: Array<string>, kept: Array<string>) {
  const isNarrowed = kept.length > 0;
  return { shared, isNarrowed, chosen: isNarrowed ? shared.filter((code) => kept.includes(code)) : shared };
}

/**
 * Where the bundle can sell: the regions and brands its tour is scheduled under that its hotel is on
 * sale under, narrowed to the bundle's own regions and brands when it has any.
 * An approved bundle's tour is its LTLE, which has no schedules of its own, so its base tour's are used.
 */
export default function useBundleMarkets(bundle: Bundle) {
  const tour = bundle.components.find(
    (component): component is TourBundleComponent => component.product_type === 'tour',
  );
  const hotel = bundle.components.find(
    (component): component is HotelBundleComponent => component.product_type === 'flashAccommodation',
  );

  const { data: tourOffer, isLoading: isLoadingTour, error: tourError } = useGetTourOffer(tour?.tour_offer_id);
  const baseTourOfferId = tourOffer?.services.find((service) => service.linkedTourOfferId)?.linkedTourOfferId;
  const {
    data: baseTourOffer,
    isLoading: isLoadingBase,
    error: baseError,
  } = useGetTourOffer(baseTourOfferId ?? undefined);
  const { data: hotelOffer, isLoading: isLoadingHotel, error: hotelError } = useGetOffer(hotel?.offer_id);

  const scheduledTour = baseTourOfferId ? baseTourOffer : tourOffer;
  const tourMarkets = scheduledTour ? tourScheduledMarkets(scheduledTour) : [];
  const hotelMarkets = hotelOffer ? hotelOnSaleMarkets(hotelOffer) : [];
  const shared = sharedMarkets(tourMarkets, hotelMarkets);

  const regions = narrow(marketRegions(shared), bundle.regions);
  const brands = narrow(marketBrands(shared), bundle.brands);
  const sells: Array<Market> = shared.filter(
    (market) => regions.chosen.includes(market.region) && brands.chosen.includes(market.brand),
  );

  return {
    hasTour: !!tour,
    hasHotel: !!hotel,
    tour: { regions: marketRegions(tourMarkets), brands: marketBrands(tourMarkets) },
    hotel: { regions: marketRegions(hotelMarkets), brands: marketBrands(hotelMarkets) },
    hasShared: shared.length > 0,
    regions,
    brands,
    sellsIn: marketRegions(sells),
    sellsUnder: marketBrands(sells),
    isLoading: (!!tour && (isLoadingTour || isLoadingBase)) || (!!hotel && isLoadingHotel),
    // A failed fetch leaves where it sells unknown, not empty.
    error: tourError ?? baseError ?? hotelError,
  };
}
